<template>
  <div class="min-h-screen bg-white">
    <!-- Masthead editorial -->
    <section class="overflow-hidden relative pt-32 pb-14 text-white bg-center bg-cover sm:pt-40" :style="mastheadStyle">
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <span
          class="inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white rounded-full border border-white/25 bg-white/10 backdrop-blur"
        >
          Blog Kali Sistemas
        </span>
        <h1 class="mt-5 max-w-3xl text-5xl font-black tracking-tight leading-[1.05] text-white text-shadow-hero sm:text-6xl">
          Conteúdo para quem faz a gestão acontecer.
        </h1>
        <p class="mt-4 max-w-2xl text-lg leading-8 text-white/90 text-shadow-hero-sm">
          Guias práticos, novidades fiscais e boas práticas de gestão para o seu
          negócio crescer com mais controle.
        </p>
      </div>
    </section>

    <!-- Filtro por categoria -->
    <section class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <nav class="flex overflow-x-auto relative gap-6 items-center h-16" aria-label="Filtrar por categoria">
          <button
            v-for="category in categories"
            :key="category"
            type="button"
            @click="selectedCategory = category"
            class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
            :class="{ 'text-[#006086] active': selectedCategory === category }"
          >
            {{ category }}
          </button>
        </nav>
      </div>
    </section>

    <section class="py-16">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-if="loading" class="py-20 text-center">
          <div class="mx-auto w-10 h-10 rounded-full border-2 animate-spin border-slate-200 border-t-[#006086]"></div>
          <p class="mt-4 text-sm font-medium text-gray-600">Carregando artigos do blog...</p>
        </div>

        <div v-else-if="error" class="py-16 text-center rounded-3xl border border-rose-200 bg-rose-50">
          <p class="text-base font-semibold text-rose-700">{{ error }}</p>
          <button
            type="button"
            @click="fetchPosts"
            class="inline-flex justify-center items-center px-5 py-3 mt-5 text-sm font-semibold text-white bg-rose-600 rounded-xl transition hover:bg-rose-700"
          >
            Tentar novamente
          </button>
        </div>

        <template v-else-if="filteredPosts.length > 0">
          <!-- Destaque -->
          <article
            v-if="featuredPost"
            class="overflow-hidden group cursor-pointer"
            tabindex="0"
            role="button"
            @click="goToPost(featuredPost)"
            @keydown.enter.prevent="goToPost(featuredPost)"
            @keydown.space.prevent="goToPost(featuredPost)"
          >
            <div class="grid gap-8 lg:grid-cols-2 lg:items-center">
              <div class="overflow-hidden relative rounded-3xl bg-slate-100 aspect-[16/11]">
                <img
                  v-if="featuredPost.hasImage"
                  :src="featuredPost.finalImageUrl"
                  :alt="featuredPost.title"
                  class="object-cover absolute inset-0 w-full h-full transition duration-700 group-hover:scale-105"
                  @load="handleImageLoad(featuredPost)"
                  @error="handleImageError(featuredPost)"
                />
                <div
                  v-else
                  class="flex absolute inset-0 justify-center items-center bg-gradient-to-br from-[#e6f0f4] to-white text-[#006086]"
                >
                  <span class="text-sm font-semibold">Kali Sistemas</span>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.22em] text-[#006086]">
                  Artigo em destaque · {{ featuredPost.category }}
                </p>
                <h2
                  class="mt-3 text-3xl font-black tracking-tight leading-tight text-gray-900 transition sm:text-4xl group-hover:text-[#006086]"
                >
                  {{ featuredPost.title }}
                </h2>
                <div class="mt-4 text-base leading-7 text-gray-600" v-html="featuredPost.excerptHtml"></div>
                <div class="flex flex-wrap gap-2 items-center mt-6 text-sm text-gray-500">
                  <span class="font-semibold text-gray-800">{{ formatAuthors(featuredPost.authors) }}</span>
                  <span aria-hidden="true" class="w-1 h-1 rounded-full bg-gray-300"></span>
                  <time :datetime="featuredPost.datetime">{{ featuredPost.date }}</time>
                  <span aria-hidden="true" class="w-1 h-1 rounded-full bg-gray-300"></span>
                  <span>{{ featuredPost.readingTime }} min de leitura</span>
                </div>
                <span class="inline-flex items-center mt-6 text-sm font-semibold text-[#006086]">
                  Ler artigo
                  <span aria-hidden="true" class="ml-1 transition group-hover:translate-x-1">→</span>
                </span>
              </div>
            </div>
          </article>

          <!-- Grade -->
          <div v-if="secondaryPosts.length" class="grid gap-x-8 gap-y-12 mt-16 sm:grid-cols-2 lg:grid-cols-3">
            <article
              v-for="post in secondaryPosts"
              :key="post.id"
              class="group cursor-pointer"
              tabindex="0"
              role="button"
              @click="goToPost(post)"
              @keydown.enter.prevent="goToPost(post)"
              @keydown.space.prevent="goToPost(post)"
            >
              <div class="overflow-hidden relative rounded-2xl bg-slate-100 aspect-[16/10]">
                <img
                  v-if="post.hasImage"
                  :src="post.finalImageUrl"
                  :alt="post.title"
                  class="object-cover absolute inset-0 w-full h-full transition duration-700 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  @load="handleImageLoad(post)"
                  @error="handleImageError(post)"
                />
                <div
                  v-else
                  class="flex absolute inset-0 justify-center items-center bg-gradient-to-br from-[#e6f0f4] to-white text-[#006086]"
                >
                  <span class="text-sm font-semibold">Kali Sistemas</span>
                </div>
              </div>

              <p class="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#006086]">
                {{ post.date }} · {{ post.category }}
              </p>
              <h3
                class="mt-2 text-xl font-black leading-tight text-gray-900 transition group-hover:text-[#006086]"
              >
                {{ post.title }}
              </h3>
              <div class="mt-2 text-sm leading-6 text-gray-600 line-clamp-3" v-html="post.excerptHtml"></div>
              <span class="inline-flex items-center mt-3 text-sm font-semibold text-[#006086]">
                Ler mais
                <span aria-hidden="true" class="ml-1 transition group-hover:translate-x-1">→</span>
              </span>
            </article>
          </div>

          <div v-if="totalPages > 1" class="flex justify-center mt-16">
            <nav
              class="inline-flex flex-wrap gap-2 items-center px-3 py-3 bg-white rounded-full border shadow-sm border-slate-200"
              aria-label="Paginação"
            >
              <button
                type="button"
                @click="prevPage"
                :disabled="page === 1"
                class="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full border transition border-slate-200 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                v-for="p in visiblePages"
                :key="p"
                type="button"
                @click="goToPage(p)"
                :aria-current="p === page ? 'page' : undefined"
                :class="[
                  'h-10 min-w-10 rounded-full px-3 text-sm font-semibold transition',
                  p === page ? 'bg-[#006086] text-white shadow' : 'text-slate-700 hover:bg-slate-100',
                ]"
              >
                {{ p }}
              </button>
              <button
                type="button"
                @click="nextPage"
                :disabled="page === totalPages"
                class="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full border transition border-slate-200 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Próxima
              </button>
            </nav>
          </div>
        </template>

        <div v-else class="py-20 text-center rounded-3xl border border-slate-200 bg-slate-50">
          <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#e6f0f4] text-[#006086]">
            <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.8"
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
              />
            </svg>
          </div>
          <h3 class="mt-5 text-2xl font-black text-gray-900">Nenhum post encontrado</h3>
          <p class="mx-auto mt-3 max-w-xl text-sm leading-7 text-gray-600 sm:text-base">
            {{
              selectedCategory === "Todos"
                ? "Ainda não há posts publicados nesta biblioteca."
                : `Não há artigos na categoria "${selectedCategory}" no momento.`
            }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "BlogPage",
};
</script>

<script setup>
import {
  ref,
  computed,
  onMounted,
  nextTick,
  onBeforeUnmount,
  watch,
} from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import { getPosts } from "@/services/blog";
import { makeCardExcerpt } from "@/services/sanitize";
import blogHeroBg from "@/assets/hero-blog.webp";
import { useSeo } from "@/composables/useSeo";

useSeo({
  title: "Blog | Kali Sistemas",
  description:
    "Insights, novidades e guias para empresas que querem crescer com tecnologia.",
  image: blogHeroBg,
});

const mastheadStyle = {
  backgroundImage:
    "linear-gradient(to right, rgba(15,23,42,0.88) 0%, rgba(15,23,42,0.74) 35%, rgba(15,23,42,0.35) 60%, rgba(15,23,42,0) 82%), url(" +
    blogHeroBg +
    ")",
};

// Atualização automática de cache (5 minutos)
const AUTO_REFRESH_MS = 5 * 60 * 1000;
// Cache em memória para imagens já carregadas nesta sessão
const loadedImages = new Set();

const selectedCategory = ref("Todos");
const categories = ref(["Todos"]);
const posts = ref([]);
const loading = ref(false);
const error = ref(null);
const router = useRouter();
const route = useRoute();
const cacheKey = "blog_state_v3";
// Estado de paginação
const page = ref(1);
const perPage = ref(50);
const totalPages = ref(1);
const totalItems = ref(0);
let refreshTimer = null;

async function fetchPosts() {
  const currentCategory =
    selectedCategory.value === "Todos" ? undefined : selectedCategory.value;
  // Força consulta de rede em refresh para refletir total e itens publicados
  loading.value = true;
  error.value = null;
  try {
    const { data, meta } = await getPosts(
      { category: currentCategory, page: page.value, perPage: perPage.value },
      {
        staleWhileRevalidate: false,
        // Atualiza automaticamente quando a revalidação terminar
        onUpdate: async (fresh) => {
          try {
            const primary = Array.isArray(fresh?.data)
              ? fresh.data
              : fresh || [];
            let combinedFresh = primary;
            const mFresh = fresh?.meta || null;
            const totalPagesFresh =
              Number(mFresh?.last_page || mFresh?.total_pages || 1) || 1;
            const perPageFresh =
              Number(mFresh?.per_page || perPage.value) || perPage.value;
            if (totalPagesFresh > 1) {
              const promises = [];
              for (let p = 2; p <= totalPagesFresh; p++) {
                promises.push(
                  getPosts({
                    category: currentCategory,
                    page: p,
                    perPage: perPageFresh,
                  }),
                );
              }
              const extras = await Promise.all(promises);
              const extraLists = extras.map((r) =>
                Array.isArray(r?.data?.data) ? r.data.data : r?.data || [],
              );
              combinedFresh = primary.concat(...extraLists);
            }
            combinedFresh = combinedFresh.filter((p) => !!p.published);
            posts.value = normalizePosts(combinedFresh);
            totalItems.value =
              Number(
                mFresh?.total || mFresh?.total_items || combinedFresh.length,
              ) || combinedFresh.length;
            totalPages.value = 1;
            page.value = 1;
            const cats = Array.from(
              new Set(
                posts.value
                  .map((p) =>
                    typeof p.category === "object"
                      ? p.category?.name || p.category?.slug
                      : p.category,
                  )
                  .filter(Boolean),
              ),
            );
            categories.value = ["Todos", ...cats];
          } catch (e) {
            void e;
          }
        },
      },
    );
    // Helper: normaliza url de imagem aceita path relativo, absoluto e com/sem prefixo storage
    const list = Array.isArray(data?.data) ? data.data : data || [];
    let combined = list;
    // Atualiza metadados de paginação da resposta principal
    const m = meta || data?.meta || null;
    totalItems.value =
      Number(m?.total || m?.total_items || list.length) || list.length;
    totalPages.value = Number(m?.last_page || m?.total_pages || 1) || 1;
    page.value = Number(m?.current_page || page.value) || page.value;
    perPage.value = Number(m?.per_page || perPage.value) || perPage.value;
    if (totalPages.value > 1) {
      const promises = [];
      for (let p = 2; p <= totalPages.value; p++) {
        promises.push(
          getPosts({
            category: currentCategory,
            page: p,
            perPage: perPage.value,
          }),
        );
      }
      const extras = await Promise.all(promises);
      const extraLists = extras.map((r) =>
        Array.isArray(r?.data?.data) ? r.data.data : r?.data || [],
      );
      combined = combined.concat(...extraLists);

      combined = combined.filter((p) => !!p.published);
      totalItems.value = combined.length;
      totalPages.value = 1;
      page.value = 1;
    }
    posts.value = normalizePosts(combined.filter((p) => !!p.published));
    // Atualiza categorias dinamicamente com base nos posts retornados (somente nomes)
    const cats = Array.from(
      new Set(
        posts.value
          .map((p) =>
            typeof p.category === "object"
              ? p.category?.name || p.category?.slug
              : p.category,
          )
          .filter(Boolean),
      ),
    );
    categories.value = ["Todos", ...cats];
    // Precarrega imagens dos posts atuais (aproveita cache do navegador)
    preloadImages(posts.value);
  } catch (e) {
    error.value = "Não foi possível carregar os posts agora.";
    console.error("Erro ao carregar posts:", e);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  // Categoria vinda por query string (ex.: link "Categorias" da página do post)
  const queryCategory = String(route.query.category || "").trim();
  if (queryCategory) {
    selectedCategory.value = queryCategory;
  }

  try {
    // Migração simples: remove cache antigo sem excerptHtml
    try {
      sessionStorage.removeItem("blog_state_v1");
    } catch (e) {
      void e;
    }
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
    const isFresh =
      cached &&
      cached.timestamp &&
      Date.now() - Number(cached.timestamp) < AUTO_REFRESH_MS;
    if (cached && Array.isArray(cached.posts) && isFresh) {
      // Categoria da URL tem prioridade sobre o cache da sessão anterior
      selectedCategory.value =
        queryCategory || cached.selectedCategory || "Todos";
      page.value = Number(cached.page || 1) || 1;
      perPage.value = Number(cached.perPage || 9) || 9;
      // Garante que posts restaurados tenham excerptHtml
      const restored = (cached.posts || []).map((p) => ({
        ...p,
        excerptHtml: p.excerptHtml ?? makeCardExcerpt(p.content || ""),
      }));
      posts.value = restored;
      const cats = Array.from(
        new Set(
          posts.value
            .map((p) =>
              typeof p.category === "object"
                ? p.category?.name || p.category?.slug
                : p.category,
            )
            .filter(Boolean),
        ),
      );
      categories.value = ["Todos", ...cats];
      loading.value = false;
      error.value = null;
      // Mesmo com cache fresco, agenda revalidação em 5 min
      // e realiza uma verificação leve agora (SWR cuida do resto)
      fetchPosts();
    } else {
      fetchPosts();
    }
  } catch {
    fetchPosts();
  }

  // Otimização: Pre-carrega imagens padrão para posts sem imagem
  nextTick(() => {
    const defaultImg = new Image();
    defaultImg.src = "/assets/hero-bg2.png";
  });

  // Timer de atualização automática do cache
  refreshTimer = setInterval(() => {
    fetchPosts();
  }, AUTO_REFRESH_MS);
});

function formatAuthors(authors) {
  if (!authors?.length) return ''
  if (authors.length === 1) return authors[0].name
  if (authors.length === 2) return `${authors[0].name}, e ${authors[1].name}`
  const all = authors.map(a => a.name)
  return all.slice(0, -1).join(', ') + ', e ' + all[all.length - 1]
}

function persistCache() {
  try {
    const snapshot = {
      selectedCategory: selectedCategory.value,
      categories: categories.value,
      posts: posts.value,
      page: page.value,
      perPage: perPage.value,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(snapshot));
  } catch (e) {
    void e;
  }
}

onBeforeRouteLeave(() => {
  persistCache();
});

onBeforeUnmount(() => {
  persistCache();
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
});

// Função para tratar erros de carregamento de imagens
function handleImageLoad(post) {
  console.log(
    `✅ Imagem carregada com sucesso para post ${post.id}: ${post.imageUrl}`,
  );
  post.imageLoading = false;
  post.imageError = false;
  if (post.finalImageUrl) {
    loadedImages.add(post.finalImageUrl);
  }
}

function handleImageError(post) {
  console.warn(
    `❌ Erro ao carregar imagem para post ${post.id}: ${post.imageUrl}`,
  );
  post.imageLoading = false;
  post.imageError = true;
  post.finalImageUrl = "/assets/hero-bg2.png";
}

// Filtra instantaneamente no cliente conforme a categoria selecionada
const filteredPosts = computed(() => {
  const all = posts.value;
  const cat = selectedCategory.value;
  if (!cat || cat === "Todos") return all;
  const q = String(cat).toLowerCase();
  return all.filter((p) => String(p.category || "").toLowerCase() === q);
});

const featuredPost = computed(() => filteredPosts.value[0] || null);
const secondaryPosts = computed(() => filteredPosts.value.slice(1));

// Páginas visíveis (até 5 ao redor da atual)
const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = page.value;
  const span = 5;
  if (total <= span) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, current - 2);
  const end = Math.min(total, start + span - 1);
  const offsetStart = Math.max(1, end - span + 1);
  return Array.from(
    { length: end - offsetStart + 1 },
    (_, i) => offsetStart + i,
  );
});

function goToPage(p) {
  const target = Math.max(1, Math.min(Number(p) || 1, totalPages.value));
  if (target === page.value) return;
  page.value = target;
  fetchPosts();
}

function nextPage() {
  if (page.value < totalPages.value) {
    page.value += 1;
    fetchPosts();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value -= 1;
    fetchPosts();
  }
}

// Atualiza listagem ao mudar categoria
watch(selectedCategory, () => {
  page.value = 1;
  fetchPosts();
});

// Navegação para página dedicada do post

function goToPost(post) {
  if (!post?.slug) return;
  router.push({ name: "blog-detail", params: { slug: post.slug } });
}

// Utilitário local: normaliza estrutura de posts e URLs de imagem
function normalizeImageUrl(raw) {
  const str = (raw || "").trim();
  if (!str) return null;
  if (/^https?:\/\//i.test(str)) return str;
  const cleaned = str.replace(/^\/+/, "");
  const withoutStorage = cleaned.replace(/^storage\/+/, "");
  const apiBase =
    import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8001/api";
  const assetBase = String(apiBase).replace(/\/?api\/?$/, "");
  return `${assetBase}/storage/${withoutStorage}`;
}

function normalizePosts(list) {
  return (list || []).map((p) => ({
    id: p.id,
    title: p.title || "Sem título",
    // Excerpt HTML seguro para cards: mantém parágrafos e <br>, sem tags cruas
    excerptHtml: makeCardExcerpt(p.content || ""),
    content: p.content || "",
    hasImage: !!(p.image_path && p.image_path.trim()),
    imageUrl: normalizeImageUrl(p.image_path),
    finalImageUrl: normalizeImageUrl(p.image_path) || "/assets/hero-bg2.png",
    imageLoading: (() => {
      const url = normalizeImageUrl(p.image_path);
      if (!url) return false;
      return !loadedImages.has(url);
    })(),
    imageError: false,
    category:
      p && p.category && typeof p.category === "object"
        ? p.category?.name || p.category?.slug || "Geral"
        : p.category && typeof p.category === "string" && p.category.trim()
          ? p.category.trim()
          : "Geral",
    slug: p.slug,
    author: {
      name:
        p.author_display_name ||
        p.author_name ||
        "Equipe Kali",
      imageUrl:
        normalizeImageUrl(p.author_avatar_url) ||
        "/assets/avatar.png",
    },
    authors: Array.isArray(p.authors) && p.authors.length
      ? p.authors.map(a => ({
          name: a.name || "Equipe Kali",
          imageUrl: normalizeImageUrl(a.avatar_url) || "/assets/avatar.png",
        }))
      : [{
          name: p.author_display_name || p.author_name || "Equipe Kali",
          imageUrl: normalizeImageUrl(p.author_avatar_url) || "/assets/avatar.png",
        }],
    date: p.published_at
      ? new Date(p.published_at).toLocaleDateString("pt-BR")
      : new Date(p.created_at).toLocaleDateString("pt-BR"),
    datetime: p.published_at || p.created_at,
    readingTime: Math.ceil((p.content || "").length / 1000) || 3,
    published: p.published,
  }));
}

// Pré-carrega imagens e marca como carregadas se o navegador já tiver em cache
function preloadImages(list) {
  (list || []).forEach((post) => {
    const url = post?.finalImageUrl;
    if (!url || loadedImages.has(url)) return;
    try {
      const img = new Image();
      img.src = url;
      if (img.complete) {
        loadedImages.add(url);
        post.imageLoading = false;
      } else {
        img.onload = () => {
          loadedImages.add(url);
          post.imageLoading = false;
        };
        img.onerror = () => {
          // Mantém estado de erro quando aplicável
        };
      }
    } catch (e) {
      void e;
    }
  });
}
</script>

<style scoped>
.text-shadow-hero {
  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.65),
    0 1px 2px rgba(0, 0, 0, 0.55);
}

.text-shadow-hero-sm {
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
}
</style>
